/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Camera, Sparkles, Loader2, ChevronLeft, AlertTriangle, Send, Stethoscope, CheckCircle2 } from 'lucide-react';
import { getWoundCase, addWoundPhoto, analyzeWoundCase, addWoundNote, closeWoundCase } from '@/lib/sprint4';
import { fileToBase64 } from '@/lib/sprint2';
import { useAuth } from '@/lib/auth';
import { BODY_REGIONS, WOUND_TYPES, StageBadge } from '@/components/woundcare/meta';

export const WoundCaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wcase, setWcase] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingPhoto, setAddingPhoto] = useState(false);
  const inputRef = useRef(null);

  const isNurse = user?.category === 'infermiere' || user?.category?.includes('infermier') || user?.registry_type === 'opi';

  const load = useCallback(async () => {
    try {
      const c = await getWoundCase(caseId);
      setWcase(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Case non accessibile');
      navigate('/wound-care');
    }
  }, [caseId, navigate]);
  useEffect(() => { load(); }, [load]);

  const analyzeLatest = async () => {
    setAnalyzing(true);
    try {
      await analyzeWoundCase(caseId);
      toast.success('Analisi aggiornata');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore analisi'); }
    finally { setAnalyzing(false); }
  };

  const uploadFollowup = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error('Max 6 MB'); return; }
    setAddingPhoto(true);
    try {
      const { base64, mime } = await fileToBase64(file);
      await addWoundPhoto(caseId, { image_base64: base64, mime_type: mime });
      toast.success('Foto aggiunta');
      await load();
    } catch (err) { toast.error('Errore upload'); }
    finally { setAddingPhoto(false); }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addWoundNote(caseId, noteText.trim());
      setNoteText('');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const close = async () => {
    if (!window.confirm('Chiudere il case? Non potrai più aggiungere foto o note.')) return;
    try {
      await closeWoundCase(caseId);
      toast.success('Case chiuso');
      await load();
    } catch (err) { toast.error('Errore'); }
  };

  if (!wcase) return <div className="p-6"><Skeleton className="h-40 w-full" /></div>;

  const latestAnalysis = ((wcase.photos || []).slice(-1)[0] || {}).analysis;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="wound-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/wound-care')}><ChevronLeft className="h-5 w-5" /></Button>
        <div>
          <h1 className="font-display text-xl font-bold capitalize">Wound care — {BODY_REGIONS.find((b) => b[0] === wcase.body_region)?.[1] || wcase.body_region}</h1>
          <div className="text-xs text-muted-foreground">Aperto {new Date(wcase.created_at).toLocaleString('it')} · {wcase.status === 'open' ? 'Attivo' : 'Chiuso'}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {wcase.status === 'open' && (
            <>
              <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={addingPhoto} data-testid="wound-add-photo-btn">
                {addingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Aggiungi foto
              </Button>
              <Button variant="outline" onClick={analyzeLatest} disabled={analyzing} data-testid="wound-analyze-btn">
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Rianalizza
              </Button>
              <Button variant="outline" onClick={close}><CheckCircle2 className="h-4 w-4" /> Chiudi case</Button>
            </>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFollowup(e.target.files?.[0])} />
        </div>
      </div>

      {latestAnalysis && !latestAnalysis.error && (
        <Card className="border-primary/30 bg-primary/5" data-testid="wound-analysis-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider">
              <Sparkles className="h-3 w-3" /> Analisi AI ultima foto
            </div>
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 text-sm">
              {latestAnalysis.npuap_stage && latestAnalysis.npuap_stage !== 'not_applicable' && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Stadio NPUAP</span><div><StageBadge stage={latestAnalysis.npuap_stage} /></div></div>
              )}
              {latestAnalysis.wound_type_hint && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Tipo</span><div>{WOUND_TYPES.find((w) => w[0] === latestAnalysis.wound_type_hint)?.[1] || latestAnalysis.wound_type_hint}</div></div>
              )}
              {latestAnalysis.size_estimate_cm && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Dimensioni</span><div>{latestAnalysis.size_estimate_cm}</div></div>
              )}
              {latestAnalysis.exudate && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Essudato</span><div className="capitalize">{latestAnalysis.exudate} · {latestAnalysis.exudate_amount}</div></div>
              )}
              {latestAnalysis.periwound_condition && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Peri-ferita</span><div className="capitalize">{latestAnalysis.periwound_condition}</div></div>
              )}
              {typeof latestAnalysis.confidence === 'number' && (
                <div><span className="text-[10px] uppercase font-semibold text-muted-foreground">Confidence AI</span><div>{Math.round(latestAnalysis.confidence * 100)}%</div></div>
              )}
            </div>
            {latestAnalysis.recommended_dressing && (
              <div className="rounded-lg bg-card border p-3 space-y-1">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">Protocollo consigliato</div>
                <div className="text-sm"><b>Medicazione:</b> {latestAnalysis.recommended_dressing}</div>
                {latestAnalysis.cleansing && <div className="text-sm"><b>Detersione:</b> {latestAnalysis.cleansing}</div>}
                {latestAnalysis.frequency && <div className="text-sm"><b>Frequenza:</b> {latestAnalysis.frequency}</div>}
                {latestAnalysis.protocol_notes && <div className="text-xs text-muted-foreground italic">{latestAnalysis.protocol_notes}</div>}
              </div>
            )}
            {(latestAnalysis.red_flags || []).length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-900 p-3">
                <div className="flex items-center gap-2 font-semibold text-sm mb-1"><AlertTriangle className="h-4 w-4" /> Red flags</div>
                <ul className="list-disc pl-5 text-xs space-y-0.5">{latestAnalysis.red_flags.map((rf, i) => <li key={`rf-${i}-${(rf||'').slice(0,20)}`}>{rf}</li>)}</ul>
              </div>
            )}
            {latestAnalysis.needs_doctor_review && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 flex items-start gap-2">
                <Stethoscope className="h-4 w-4 mt-0.5" />
                <div className="text-xs"><b>Valutazione medica consigliata.</b> Prenota una visita con un medico specialista.</div>
              </div>
            )}
            <p className="text-[10px] italic text-muted-foreground border-t pt-2">{latestAnalysis.disclaimer}</p>
          </CardContent>
        </Card>
      )}

      {/* Photos timeline */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Timeline foto ({(wcase.photos || []).length})</h2>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {(wcase.photos || []).map((p, i) => (
            <Card key={p.id} data-testid={`wound-photo-${i}`}>
              <CardContent className="p-3 space-y-2">
                <img
                  src={p.data_base64 ? `data:${p.mime_type};base64,${p.data_base64}` : ''}
                  alt={`Foto ${i + 1}`}
                  className="w-full h-40 object-cover rounded-lg"
                />
                <div className="text-[10px] text-muted-foreground">
                  Foto {i + 1} · {new Date(p.uploaded_at).toLocaleString('it')}
                </div>
                {p.analysis?.npuap_stage && p.analysis.npuap_stage !== 'not_applicable' && (
                  <StageBadge stage={p.analysis.npuap_stage} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Nurse notes */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Note dell'infermiere</h2>
        {isNurse && wcase.status === 'open' && (
          <div className="flex gap-2">
            <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Aggiungi una nota clinica..." data-testid="wound-note-input" />
            <Button onClick={submitNote} disabled={!noteText.trim()}><Send className="h-4 w-4" /></Button>
          </div>
        )}
        {(wcase.notes || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Nessuna nota clinica ancora.</div>
        ) : (
          <div className="space-y-2">
            {(wcase.notes || []).slice().reverse().map((n) => (
              <Card key={n.id}><CardContent className="p-3 text-sm space-y-1">
                <div className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString('it')}</div>
                <div className="whitespace-pre-wrap">{n.text}</div>
              </CardContent></Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
